import { parseProductionCents } from '@/api/boundaryAdapter';
import {
  applyConfirmationFlag,
  deriveAppointmentStatus,
  deriveConfirmationFlag,
  normalizeFlagKey,
} from '@/features/calendar/adapters/calendarAdapters';
import type {
  BirdEyeBoardState,
  BirdEyeKpiExtras,
  BadgeTone,
  BirdEyeFinishedPatient,
} from '@elva/ui';
import type { FlagKey } from '@elva/ui';
import type {
  BirdEyeArrivingItem,
  BirdEyeCellRaw,
  BirdEyeFinished,
  BirdEyeFinishedResponse,
  BirdEyeOperatoryItem,
  BirdEyeOperatoryRaw,
  BirdEyeResponse,
  BirdEyeSeated,
  BirdEyeVisitStatus,
  BirdEyeWaitingItem,
  BirdEyeWalkingOutItem,
} from '../api/types';

const ARRIVING_WINDOW_MS = 2 * 60 * 60 * 1000;
const ACTIVE_OP_STATUSES = new Set(['CHECKED_IN', 'ARRIVED', 'TREATMENT']);
const WALKING_STATUSES = new Set(['COMPLETED', 'CHECKED_OUT']);
const CAPTURED_STATUSES = new Set(['COMPLETED']);

function mapFlags(flags: string[]): FlagKey[] {
  return flags
    .map((f) => normalizeFlagKey(f))
    .filter((f): f is FlagKey => f != null);
}

function mapOperatoryRoom(id: string, name?: string): string {
  if (name?.trim()) return name.trim();
  const num = id.replace(/^o/i, '');
  return `Op ${num}`;
}

function parseLocalDateTime(iso: string | undefined): Date | null {
  if (!iso) return null;
  const match = /(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/.exec(iso);
  if (match) {
    return new Date(`${match[1]}T${match[2]}:${match[3]}:00`);
  }
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

type BirdEyeLaneKey = 'arriving' | 'waiting' | 'seated' | 'walking' | 'finished';

/**
 * FD80 — the server owns the lane. Lane names aren't strictly documented, so
 * match loosely and fall back to `visit_state` when `lane` is missing/unknown.
 */
function matchLaneKey(value: string | undefined): BirdEyeLaneKey | null {
  const key = value?.toLowerCase().replace(/[^a-z]/g, '');
  if (!key) return null;
  if (key.includes('wait') || key === 'arrived' || key === 'checkedin') return 'waiting';
  if (key.includes('walk')) return 'walking';
  if (key.includes('finish') || key === 'checkedout' || key === 'done') return 'finished';
  if (
    key.includes('seat') ||
    key.includes('chair') ||
    key.includes('treat') ||
    key.includes('operatory') ||
    key === 'op' ||
    key === 'operatory' ||
    key === 'inop'
  ) {
    return 'seated';
  }
  if (key.includes('arriv') || key.includes('expect') || key === 'scheduled') return 'arriving';
  return null;
}

const OFF_BOARD_STATUSES = new Set(['NO_SHOW', 'BROKEN', 'RESCHED']);

/**
 * Explicit `lane` wins. Without one, a booking that is no longer on the books
 * (cancelled / no-show / rescheduled) is off the board; otherwise `visit_state`
 * places the patient.
 */
function resolveServerLane(raw: BirdEyeCellRaw): BirdEyeLaneKey | null {
  const explicit = matchLaneKey(raw.lane);
  if (explicit) return explicit;
  if (OFF_BOARD_STATUSES.has(deriveAppointmentStatus(raw) ?? '')) return 'finished';
  return matchLaneKey(raw.visit_state);
}

function minutesSince(iso: string | null | undefined, now: Date): number | null {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  return Math.max(0, Math.round((now.getTime() - then.getTime()) / 60_000));
}

interface NormalizedBirdEyeCell {
  appointment_id: number;
  patient_name: string;
  provider_name: string;
  operatory_name: string;
  start: Date;
  end: Date;
  status: string;
  status_label: string;
  procedure: string;
  production_cents: number;
  flags: FlagKey[];
  rollup_color?: string;
  lane: BirdEyeLaneKey | null;
  visit_state?: string | undefined;
  arrived_at?: string | null | undefined;
  seated_at?: string | null | undefined;
  checked_out_at?: string | null | undefined;
  treatment_done_at?: string | null | undefined;
  waiting_minutes?: number | null | undefined;
  chair_minutes?: number | null | undefined;
}

function normalizeBirdEyeCell(raw: BirdEyeCellRaw): NormalizedBirdEyeCell | null {
  const appointment_id = raw.appointment_id ?? raw.id;
  if (appointment_id == null) return null;

  const start = parseLocalDateTime(raw.start_local);
  if (!start) return null;

  const duration_min = raw.duration_minutes ?? 60;
  const end =
    parseLocalDateTime(raw.end_local) ??
    new Date(start.getTime() + duration_min * 60 * 1000);

  const procedure =
    (typeof raw.procedure === 'string' && raw.procedure.trim() ? raw.procedure : '') ||
    raw.appointment_reason ||
    '';

  return {
    appointment_id,
    patient_name: raw.patient_name ?? 'Unknown',
    provider_name: raw.provider_name ?? '',
    operatory_name: raw.operatory_name ?? '',
    start,
    end,
    status: (deriveAppointmentStatus(raw) ?? 'SCHEDULED').toUpperCase(),
    status_label: raw.status_label ?? raw.status ?? 'Scheduled',
    procedure,
    production_cents:
      raw.production_cents ?? parseProductionCents(raw.production) ?? 0,
    flags: applyConfirmationFlag(mapFlags(raw.flags ?? []), deriveConfirmationFlag(raw)),
    ...(raw.rollup_color ? { rollup_color: raw.rollup_color } : {}),
    lane: resolveServerLane(raw),
    visit_state: raw.visit_state,
    arrived_at: raw.arrived_at,
    seated_at: raw.seated_at,
    checked_out_at: raw.checked_out_at,
    treatment_done_at: raw.treatment_done_at,
    waiting_minutes: raw.waiting_minutes,
    chair_minutes: raw.chair_minutes,
  };
}

function flattenOperatoryCells(operatories: BirdEyeOperatoryRaw[]): NormalizedBirdEyeCell[] {
  const cells: NormalizedBirdEyeCell[] = [];
  operatories.forEach((op) => {
    (op.cells ?? []).forEach((raw) => {
      const normalized = normalizeBirdEyeCell({
        ...raw,
        ...(raw.operatory_name ?? op.operatory_name
          ? { operatory_name: raw.operatory_name ?? op.operatory_name }
          : {}),
        operatory_column_id: raw.operatory_column_id ?? op.operatory_column_id,
      });
      if (normalized) cells.push(normalized);
    });
  });
  return cells.sort((a, b) => a.start.getTime() - b.start.getTime());
}

function rollupToBadgeTone(rollup?: string): BadgeTone {
  switch (rollup?.toLowerCase()) {
    case 'r':
    case 'red':
      return 'danger';
    case 'y':
    case 'yellow':
      return 'gold';
    case 'g':
    case 'green':
      return 'success';
    default:
      return 'neutral';
  }
}

function arrivingPatient(p: BirdEyeArrivingItem) {
  return {
    id: String(p.appointment_id),
    patient: p.patient,
    appt: `${p.eta_minutes}m`,
    prov: p.provider,
    proc: p.procedure,
    value: 0,
    flags: mapFlags(p.flags ?? []),
    badge: 'Arriving',
    bt: 'neutral' as const,
  };
}

function waitingPatient(p: BirdEyeWaitingItem) {
  const bt =
    p.alarm === 'red' ? ('danger' as const) : p.alarm === 'yellow' ? ('gold' as const) : ('neutral' as const);
  return {
    id: String(p.appointment_id),
    patient: p.patient,
    appt: `${p.wait_minutes}m wait`,
    prov: '',
    proc: p.status,
    value: 0,
    flags: mapFlags(p.flags ?? []),
    badge: 'Waiting',
    bt,
  };
}

function seatedPatient(p: BirdEyeSeated) {
  return {
    id: String(p.appointment_id),
    patient: p.patient,
    appt: `${p.in_op_minutes}m`,
    prov: '',
    proc: p.status,
    value: 0,
    flags: [] as FlagKey[],
    badge: 'In op',
    bt: 'success' as const,
  };
}

function walkingPatient(p: BirdEyeWalkingOutItem) {
  return {
    id: String(p.appointment_id),
    patient: p.patient,
    appt: `${p.out_minutes}m`,
    prov: '',
    proc: p.status,
    value: 0,
    flags: [] as FlagKey[],
    badge: 'Walking out',
    bt: 'neutral' as const,
  };
}

function cellToArriving(cell: NormalizedBirdEyeCell, now: Date) {
  const etaMin = Math.max(1, Math.round((cell.start.getTime() - now.getTime()) / 60_000));
  return {
    id: String(cell.appointment_id),
    patient: cell.patient_name,
    appt: `${etaMin}m`,
    prov: cell.provider_name,
    proc: cell.procedure || cell.status_label,
    value: Math.round(cell.production_cents / 100),
    flags: cell.flags,
    badge: 'Arriving',
    bt: rollupToBadgeTone(cell.rollup_color),
  };
}

function waitingMinutes(cell: NormalizedBirdEyeCell, now: Date): number {
  return (
    cell.waiting_minutes ??
    minutesSince(cell.arrived_at, now) ??
    Math.max(1, Math.round((now.getTime() - cell.start.getTime()) / 60_000))
  );
}

function visitKey(visit: string | undefined): string {
  return visit?.toUpperCase().replace(/[^A-Z]/g, '') ?? '';
}

function cellToWaiting(cell: NormalizedBirdEyeCell, now: Date) {
  const waitMin = waitingMinutes(cell, now);
  return {
    id: String(cell.appointment_id),
    patient: cell.patient_name,
    appt: `${waitMin}m wait`,
    prov: cell.provider_name,
    proc: cell.procedure || cell.status_label,
    value: Math.round(cell.production_cents / 100),
    flags: cell.flags,
    badge: visitKey(cell.visit_state) === 'CHECKEDIN' ? 'Checked in' : 'Waiting',
    bt: rollupToBadgeTone(cell.rollup_color),
  };
}

/** In the chair the visit state matters: treatment done means "ready to check out", not still working. */
function cellToSeated(cell: NormalizedBirdEyeCell, now: Date) {
  const inOpMin =
    cell.chair_minutes ??
    minutesSince(cell.seated_at, now) ??
    Math.max(1, Math.round((now.getTime() - cell.start.getTime()) / 60_000));
  const treatmentDone = visitKey(cell.visit_state) === 'TREATMENTDONE';
  return {
    id: String(cell.appointment_id),
    patient: cell.patient_name,
    appt: `${inOpMin}m`,
    prov: cell.provider_name,
    proc: cell.procedure || cell.status_label,
    value: Math.round(cell.production_cents / 100),
    flags: cell.flags,
    badge: treatmentDone ? 'Treatment done' : 'In op',
    bt: treatmentDone ? ('gold' as const) : ('success' as const),
  };
}

function cellToWalking(cell: NormalizedBirdEyeCell, now: Date) {
  const outMin =
    minutesSince(cell.checked_out_at ?? cell.treatment_done_at, now) ??
    Math.max(1, Math.round((now.getTime() - cell.end.getTime()) / 60_000));
  return {
    id: String(cell.appointment_id),
    patient: cell.patient_name,
    appt: `${outMin}m`,
    prov: cell.provider_name,
    proc: cell.procedure || cell.status_label,
    value: Math.round(cell.production_cents / 100),
    flags: cell.flags,
    badge: 'Walking out',
    bt: 'neutral' as const,
  };
}

function findSeatedCell(cells: NormalizedBirdEyeCell[], now: Date): NormalizedBirdEyeCell | null {
  const inWindow = cells.find((c) => now >= c.start && now < c.end);
  if (inWindow && ACTIVE_OP_STATUSES.has(inWindow.status)) return inWindow;
  if (inWindow) return inWindow;
  return cells.find((c) => ACTIVE_OP_STATUSES.has(c.status)) ?? null;
}

export interface AdaptBirdEyeOptions {
  /**
   * appointment id → index of the room the staff member dropped the patient onto.
   * The status endpoint can't set an operatory, so an unassigned seated patient would
   * otherwise jump to an arbitrary room on the next refresh.
   */
  preferredRoomByAppointment?: ReadonlyMap<number, number>;
}

function adaptBirdEyeOperatoryBoard(
  operatories: BirdEyeOperatoryRaw[],
  now = new Date(),
  options: AdaptBirdEyeOptions = {},
): BirdEyeBoardState {
  const allCells = flattenOperatoryCells(operatories);
  const seatedIds = new Set<number>();

  // The column with no operatory only carries unassigned appointments; they
  // still feed the lanes below but it isn't a room.
  const rooms = operatories.filter((op) => op.operatory_id != null || op.operatory_name?.trim());

  const seatedByRoom: (NormalizedBirdEyeCell | null)[] = rooms.map((op) => {
    const opCells = (op.cells ?? [])
      .map((raw) =>
        normalizeBirdEyeCell({
          ...raw,
          ...(raw.operatory_name ?? op.operatory_name
            ? { operatory_name: raw.operatory_name ?? op.operatory_name }
            : {}),
        }),
      )
      .filter((c): c is NormalizedBirdEyeCell => c != null)
      .sort((a, b) => a.start.getTime() - b.start.getTime());

    const serverSeated = opCells.find((c) => c.lane === 'seated');
    const seatedCell = serverSeated ?? (opCells.some((c) => c.lane) ? null : findSeatedCell(opCells, now));
    if (seatedCell) seatedIds.add(seatedCell.appointment_id);
    return seatedCell;
  });

  // A seated patient with no operatory of their own (or a second one in an occupied room)
  // still has to be shown in a chair — otherwise they match no lane and vanish.
  const overflowSeatedIds = new Set<number>();
  allCells
    .filter((c) => c.lane === 'seated' && !seatedIds.has(c.appointment_id))
    .forEach((cell) => {
      const preferred = options.preferredRoomByAppointment?.get(cell.appointment_id);
      const index =
        preferred != null && seatedByRoom[preferred] === null
          ? preferred
          : seatedByRoom.indexOf(null);
      if (index >= 0) {
        seatedByRoom[index] = cell;
        seatedIds.add(cell.appointment_id);
      } else {
        overflowSeatedIds.add(cell.appointment_id);
      }
    });

  const ops = rooms.map((op, index) => {
    const seatedCell = seatedByRoom[index];
    return {
      room: mapOperatoryRoom(op.operatory_column_id, op.operatory_name),
      seated: seatedCell ? cellToSeated(seatedCell, now) : null,
    };
  });

  // The server-owned `lane` wins; the time-based rules only apply to cells
  // the backend didn't classify (older payloads).
  const arriving = allCells
    .filter((c) => {
      if (seatedIds.has(c.appointment_id)) return false;
      if (c.lane) return c.lane === 'arriving';
      if (OFF_BOARD_STATUSES.has(c.status)) return false;
      const diff = c.start.getTime() - now.getTime();
      return diff > 0 && diff <= ARRIVING_WINDOW_MS;
    })
    .slice(0, 12)
    .map((c) => cellToArriving(c, now));

  const waiting = allCells
    .filter((c) => {
      if (seatedIds.has(c.appointment_id)) return false;
      if (overflowSeatedIds.has(c.appointment_id)) return true;
      if (c.lane) return c.lane === 'waiting';
      if (c.status === 'PENDING') return true;
      return c.start.getTime() <= now.getTime() && now.getTime() < c.end.getTime() && c.status === 'ARRIVED';
    })
    .slice(0, 12)
    .map((c) => cellToWaiting(c, now));

  const walking = allCells
    .filter((c) => {
      if (c.lane) return c.lane === 'walking';
      return WALKING_STATUSES.has(c.status) && c.end.getTime() <= now.getTime();
    })
    .slice(0, 12)
    .map((c) => cellToWalking(c, now));

  return { arriving, waiting, walking, ops };
}

const EMPTY_BOARD: BirdEyeBoardState = {
  arriving: [],
  waiting: [],
  walking: [],
  ops: [],
};

export function adaptBirdEyeResponse(
  data: BirdEyeResponse,
  options?: AdaptBirdEyeOptions,
): BirdEyeBoardState {
  if (data.operatories?.length) {
    return adaptBirdEyeOperatoryBoard(data.operatories, new Date(), options);
  }

  const lanes = data.lanes;
  if (!lanes) return EMPTY_BOARD;

  return {
    arriving: (lanes.arriving ?? []).map(arrivingPatient),
    waiting: (lanes.waiting ?? []).map(waitingPatient),
    walking: (lanes.walking_out ?? []).map(walkingPatient),
    ops: (lanes.operatory ?? []).map((op: BirdEyeOperatoryItem) => ({
      room: mapOperatoryRoom(op.operatory),
      seated: op.seated ? seatedPatient(op.seated) : null,
    })),
  };
}

/**
 * Day-level KPI figures from the live payload: the server's own appointment count
 * (falling back to the cells it sent) and the mean wait of everyone in the waiting lane.
 */
export function adaptBirdEyeKpiExtras(
  data: BirdEyeResponse | undefined,
  now = new Date(),
): BirdEyeKpiExtras | undefined {
  if (!data) return undefined;

  if (data.operatories?.length) {
    const cells = flattenOperatoryCells(data.operatories);
    const waits = cells.filter((c) => c.lane === 'waiting').map((c) => waitingMinutes(c, now));
    return {
      scheduledToday: data.meta?.occupancy?.appointments_today ?? cells.length,
      avgWaitMinutes: waits.length
        ? Math.round(waits.reduce((sum, m) => sum + m, 0) / waits.length)
        : null,
    };
  }

  const lanes = data.lanes;
  if (!lanes) return undefined;
  const waits = (lanes.waiting ?? []).map((p) => p.wait_minutes);
  return {
    scheduledToday:
      data.meta?.occupancy?.appointments_today ??
      (lanes.arriving?.length ?? 0) +
        waits.length +
        (lanes.operatory ?? []).filter((op) => op.seated).length +
        (lanes.walking_out?.length ?? 0) +
        (lanes.finished?.count ?? 0),
    avgWaitMinutes: waits.length
      ? Math.round(waits.reduce((sum, m) => sum + m, 0) / waits.length)
      : null,
  };
}

export interface AdaptedBirdEyeFinishedSummary {
  total: number;
  production: number;
  lastPatient?: string;
  lastTime?: string;
}

/** Production from the finished endpoint is a dollar Decimal (string|number). */
function productionToDollars(value: string | number | undefined): number {
  if (value == null) return 0;
  const n = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

function formatFinishedTime(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/**
 * FD82 — the per-patient finished list for the "View all" modal, from the real
 * /bird-eye/finished endpoint (previously hardcoded to []).
 * `ins` is not supplied by the endpoint → false until the backend flags it.
 */
export function adaptBirdEyeFinishedList(
  resp: BirdEyeFinishedResponse | undefined,
): BirdEyeFinishedPatient[] {
  if (!resp?.finished?.length) return [];
  return resp.finished.map((row) => ({
    name: row.patient || 'Unknown',
    time: formatFinishedTime(row.checked_out_at ?? row.treatment_done_at ?? null),
    value: productionToDollars(row.production),
    ins: false,
  }));
}

/** FD82 — honest captured-only summary from the endpoint totals (not derived from the live board). */
export function adaptBirdEyeFinishedSummaryFromEndpoint(
  resp: BirdEyeFinishedResponse | undefined,
): AdaptedBirdEyeFinishedSummary | undefined {
  if (!resp?.totals) return undefined;
  const last = resp.finished?.[resp.finished.length - 1];
  return {
    total: resp.totals.finished_count,
    production: productionToDollars(resp.totals.production_captured),
    ...(last ? { lastPatient: last.patient } : {}),
    ...(last ? { lastTime: formatFinishedTime(last.checked_out_at ?? last.treatment_done_at ?? null) } : {}),
  };
}

/**
 * The only cell fields a status change is allowed to touch. The PATCH response is a
 * sparse cell (zeroed production, nulled times), so spreading all of it over the cached
 * cell wipes production or makes the cell unparseable and the card vanishes.
 */
const STATUS_CELL_FIELDS = [
  'status',
  'status_label',
  'booking_state',
  'confirmation_state',
  'confirmed_state',
  'visit_state',
  'lane',
  'is_present',
  'arrived_at',
  'seated_at',
  'treatment_done_at',
  'checked_out_at',
  'waiting_minutes',
  'chair_minutes',
  'timer_source',
  'stale',
] as const satisfies readonly (keyof BirdEyeCellRaw)[];

/** Applies a PATCH response's status fields to the cached cell, leaving everything else as is. */
export function applyBirdEyeStatusToCell(
  cell: BirdEyeCellRaw,
  patched: BirdEyeCellRaw,
): BirdEyeCellRaw {
  const status = Object.fromEntries(
    STATUS_CELL_FIELDS.filter((field) => field in patched).map((field) => [field, patched[field]]),
  );
  return { ...cell, ...status };
}

/**
 * The cell fields a status action changes, for updating the cached board before the server's
 * (possibly still rebuilding) read model catches up. The same action set is used by the
 * calendar drawer and the bird-eye board, so a change on one shows on the other at once.
 */
export function birdEyeCellPatchForStatus(
  status: BirdEyeVisitStatus,
  now: Date = new Date(),
): BirdEyeCellRaw {
  const at = now.toISOString();
  switch (status) {
    case 'ARRIVED':
      return { visit_state: 'ARRIVED', lane: 'waiting', is_present: true, arrived_at: at, waiting_minutes: null };
    case 'SEATED':
      return { visit_state: 'SEATED', lane: 'in_operatory', is_present: true, seated_at: at, chair_minutes: null };
    case 'TREATMENT_DONE':
      return { visit_state: 'TREATMENT_DONE', lane: 'in_operatory', is_present: true, treatment_done_at: at };
    case 'CHECKED_OUT':
      return { visit_state: 'CHECKED_OUT', lane: 'walking_out', is_present: true, checked_out_at: at };
    case 'NO_SHOW':
      return { booking_state: 'NO_SHOW', lane: 'finished' };
    case 'CANCELLED':
      return { booking_state: 'CANCELLED', lane: 'finished' };
    case 'RESCHEDULED':
      return { booking_state: 'RESCHEDULED', lane: 'finished' };
    case 'CONFIRMED':
      return { confirmation_state: 'CONFIRMED', confirmed_state: 'confirmed' };
  }
}

function completedCells(operatories: BirdEyeOperatoryRaw[]): NormalizedBirdEyeCell[] {
  return flattenOperatoryCells(operatories).filter((c) => CAPTURED_STATUSES.has(c.status));
}

/**
 * The finished endpoint is polled and can lag behind a status change, so an appointment
 * finished a moment ago is already `COMPLETED` on the live board but missing from its
 * totals. Add those on top, de-duplicated by appointment id, so captured production
 * sums up as soon as the visit is finished.
 */
export function adaptBirdEyeCapturedSummary(
  finished: BirdEyeFinishedResponse | undefined,
  live: BirdEyeResponse | undefined,
): AdaptedBirdEyeFinishedSummary | undefined {
  const base = adaptBirdEyeFinishedSummaryFromEndpoint(finished);
  if (!base || !live?.operatories?.length) return base ?? adaptBirdEyeFinished(live);

  const counted = new Set((finished?.finished ?? []).map((row) => row.appointment_id));
  const extra = completedCells(live.operatories).filter((c) => !counted.has(c.appointment_id));
  if (!extra.length) return base;

  const extraCents = extra.reduce((sum, c) => sum + c.production_cents, 0);
  const last = extra[extra.length - 1];
  return {
    total: base.total + extra.length,
    production: base.production + Math.round(extraCents / 100),
    ...(last ? { lastPatient: last.patient_name } : {}),
    ...(last
      ? { lastTime: formatFinishedTime(last.checked_out_at ?? last.treatment_done_at ?? null) }
      : {}),
  };
}

function finishedFromOperatoryCells(
  operatories: BirdEyeOperatoryRaw[],
): AdaptedBirdEyeFinishedSummary {
  const done = completedCells(operatories);
  const productionCents = done.reduce((sum, c) => sum + c.production_cents, 0);
  const last = done[done.length - 1];

  return {
    total: done.length,
    production: Math.round(productionCents / 100),
    ...(last ? { lastPatient: last.patient_name } : {}),
    ...(last ? { lastTime: last.status_label } : {}),
  };
}

export function adaptBirdEyeFinished(
  data: BirdEyeResponse | undefined,
): AdaptedBirdEyeFinishedSummary | undefined {
  if (!data) return undefined;

  if (data.operatories?.length) {
    return finishedFromOperatoryCells(data.operatories);
  }

  const finished = data.lanes?.finished;
  if (!finished) return undefined;

  return {
    total: finished.count ?? 0,
    production: Math.round((finished.production_cents ?? 0) / 100),
    lastPatient: finished.last?.patient,
    lastTime: finished.last?.time,
  };
}
